import { useCallback, useMemo, useState } from "react";
import { generatePath, useNavigate } from "react-router";

import type { Plan } from "@/lib/gql/__generated__/graphql";

import type { DnDResultItem } from "@/hooks/use-dnd/use-dnd";

import { PATHS } from "@/routes/paths";

import { BlankContentForList } from "@/components/features/_components";
import { PlanHeaderAndTabs } from "@/components/features/items/_components";
import {
	ListTableHeader,
	SortButton,
} from "@/components/features/items/edit/_shared/ui/components/system/list/list-table-header";
import { ItemListContainer } from "@/components/features/items/edit/_shared/ui/components/system/list/list-wrapper";

import { Table, TableBody, TableCaption } from "@/components/ui/table/table";

import { PlanListPageTableDefaultItem, PlanListPageTableSortableItem, PlanListTableHeader } from "./table";

type Props = {
	hotelID: string;
	onSaveSort: () => Promise<void>;
	onCancelSort: () => void;

	dndItemList: Array<DnDResultItem<Plan>>;
	secretPlans: Plan[];
	onDeletePlan: (planID: string) => Promise<void>;

	inUseBasic: boolean;
	showScConnectedColumn: boolean;
};

export const PlanListTable = ({
	hotelID,
	onSaveSort,
	onCancelSort,
	dndItemList,
	secretPlans,
	onDeletePlan: _onDeletePlan,
	inUseBasic,
	showScConnectedColumn,
}: Props) => {
	const navigate = useNavigate();
	const createNewItemList = useMemo(
		() => [
			{
				title: "通常プラン",
				handleClick: () => {
					navigate(generatePath(PATHS.HOTEL.ITEMS.PLAN.EDIT.NEW.PLAN.STANDARD, { hotelID }));
				},
			},
			{
				title: "日帰りプラン",
				handleClick: () => {
					navigate(`${generatePath(PATHS.HOTEL.ITEMS.PLAN.EDIT.NEW.PLAN.DAYTRIP, { hotelID })}`);
				},
				canShow: inUseBasic,
			},
		],
		[hotelID, inUseBasic, navigate],
	);

	const editPlanPagePath = useCallback(
		(planID: string, isDayTrip: boolean) =>
			isDayTrip
				? generatePath(PATHS.HOTEL.ITEMS.PLAN.EDIT.PLAN_ID.DAYTRIP.DEFAULT, { hotelID, planID })
				: generatePath(PATHS.HOTEL.ITEMS.PLAN.EDIT.PLAN_ID.DEFAULT, { hotelID, planID }),
		[hotelID],
	);
	const onTransitionToEdit = useCallback(
		(planID: string, isDayTrip: boolean) => () => {
			navigate(editPlanPagePath(planID, isDayTrip));
		},
		[editPlanPagePath, navigate],
	);
	const onDeletePlan = useCallback(
		(planID: string) => {
			return async () => {
				await _onDeletePlan(planID);
			};
		},
		[_onDeletePlan],
	);

	/**
	 * 通常・日帰りプランをリストで表示できるか
	 */
	const canShowPlanList = useMemo(() => dndItemList.length > 0, [dndItemList.length]);

	/**
	 * シークレットプランをリストで表示できるか
	 */
	const canShowSecretPlanList = useMemo(() => secretPlans.length > 0, [secretPlans.length]);

	const [isSorting, setIsSorting] = useState(false);
	const onChangeIsSorting = useCallback((isSorting: boolean) => setIsSorting(isSorting), []);

	return (
		<>
			<PlanHeaderAndTabs hotelID={hotelID} headerProps={{ title: "プラン一覧" }} />

			<ItemListContainer
				header={
					<ListTableHeader
						leftContent={
							<SortButton
								isSorting={isSorting}
								onChangeIsSorting={onChangeIsSorting}
								onClickCancelSort={onCancelSort}
								onClickSaveSort={onSaveSort}
							/>
						}
						createNewItemList={createNewItemList}
					/>
				}
				tableBody={
					<>
						<Table className="w-full min-w-[max-content]">
							<TableCaption>通常 / 日帰りプラン</TableCaption>
							{canShowPlanList ? (
								<>
									<PlanListTableHeader
										hasSortItem
										isSorting={isSorting}
										showScConnectedColumn={showScConnectedColumn}
									/>

									<TableBody>
										{dndItemList.map((result) => {
											const { planID, isDayTrip } = result.value;

											return (
												<PlanListPageTableSortableItem
													key={result.key}
													isSorting={isSorting}
													onDeletePlan={onDeletePlan(planID)}
													editPlanPagePath={editPlanPagePath(planID, isDayTrip)}
													onTransitionToEdit={onTransitionToEdit(planID, isDayTrip)}
													plan={result}
													showScConnectedColumn={showScConnectedColumn}
												/>
											);
										})}
									</TableBody>
								</>
							) : (
								<BlankContentForList
									content={`通常 / 日帰りプランがありません。\n新規作成ボタンからプランを作成してください。`}
								/>
							)}
						</Table>

						<Table className="w-full min-w-[max-content]">
							<TableCaption>シークレットプラン</TableCaption>
							{canShowSecretPlanList ? (
								<>
									<PlanListTableHeader isSorting={isSorting} showScConnectedColumn={showScConnectedColumn} />

									<TableBody>
										{secretPlans.map((item) => {
											const { planID, isDayTrip } = item;

											return (
												<PlanListPageTableDefaultItem
													key={item.planID}
													onDeletePlan={onDeletePlan(planID)}
													onTransitionToEdit={onTransitionToEdit(planID, isDayTrip)}
													editPlanPagePath={editPlanPagePath(planID, isDayTrip)}
													plan={item}
													showScConnectedColumn={showScConnectedColumn}
												/>
											);
										})}
									</TableBody>
								</>
							) : (
								<BlankContentForList content="シークレットプランがありません。" />
							)}
						</Table>
					</>
				}
			/>
		</>
	);
};
